// ─── src/api/gewOverview.ts ───────────────────────────────────
import { apiClient } from "./registration";
import type { GewParticipant } from "./gew";

export interface OverviewParticipantsPayload {
  total: number;
  founders: number;
  byGovernorate: Record<string, number>;
  byGender: Record<string, number>;
  byAgeGroup: Record<string, number>;
  byCurrentStatus: Record<string, number>;
  byStartupIndustry: Record<string, number>;
  byStartupStage: Record<string, number>;
  bySkills: Record<string, number>;
  registrationsPerDay: Array<{ date: string; count: number }>;
  data: GewParticipant[];
}

export interface OverviewPortalEventRow {
  id: string;
  slug: string | null;
  titleAr: string | null;
  eventDate: string | null;
  status: string;
  /** Seat cap; null = not set / unlimited. */
  capacity: number | null;
  total: number;
  confirmed: number;
  waitlist: number;
  accepted: number;
  rejected: number;
  pending: number;
  checkedIn: number;
}

export interface OverviewPortalPartner {
  id: string;
  nameAr: string;
  nameEn: string | null;
  type: string;
  city: string | null;
  governorate: string | null;
  status: string;
  logoUrl: string | null;
  contactName: string | null;
  contactEmail: string | null;
  createdAt: string | null;
  eventCount: number;
  publishedEventCount: number;
  /** In-window and not cancelled/draft — what the hero counts. */
  activeEventCount: number;
  totalCapacity: number;
  totalAttendees: number;
  confirmedCount: number;
  waitlistCount: number;
  acceptedCount: number;
  checkedInCount: number;
  events: OverviewPortalEventRow[];
}

export interface OverviewLegacyPartner {
  id: string;
  orgNameAr: string;
  orgNameEn: string | null;
  orgType: string;
  orgCity: string;
  coordinatorName: string | null;
  coordinatorEmail: string | null;
  coordinatorPhone: string | null;
  hasCollaboratedBefore: boolean;
  submittedAt: string | null;
}

export interface OverviewCatalystMeetupRow {
  id: string;
  title: string | null;
  eventDate: string | null;
  governorate: string | null;
  capacity: number | null;
  total: number;
  confirmed: number;
  waitlist: number;
  accepted: number;
  rejected: number;
  pending: number;
  checkedIn: number;
}

export interface OverviewCatalyst {
  hostName: string;
  hostEmail: string | null;
  meetupCount: number;
  totalCapacity: number;
  totalAttendees: number;
  confirmedCount: number;
  waitlistCount: number;
  acceptedCount: number;
  checkedInCount: number;
  meetups: OverviewCatalystMeetupRow[];
}

export interface OverviewPartnersPayload {
  portal: OverviewPortalPartner[];
  legacy: OverviewLegacyPartner[];
  catalysts: OverviewCatalyst[];
  totals: {
    portalCount: number;
    approvedPortalCount: number;
    legacyCount: number;
    catalystCount: number;
    activeEventCount: number;
    meetupCount: number;
    totalEventAttendees: number;
    totalMeetupAttendees: number;
    totalEventCapacity: number;
    totalMeetupCapacity: number;
    /** Portal partners with ≥1 published in-window event. */
    publishedPartnerCount: number;
    /** Accepted registrations across partner events + meetups. */
    totalAccepted: number;
    /** Distinct cities hosting an active event or meetup. */
    eventCityCount: number;
    /** The resolved city names behind eventCityCount. */
    eventCities: string[];
    /** Gender split across attendees (partner events + meetups, Nov 1–5). */
    attendeeGender?: AttendeeGenderSplit & {
      events: AttendeeGenderSplit;
      meetups: AttendeeGenderSplit;
    };
  };
  distributions: {
    partnersByGovernorate: Record<string, number>;
    attendeesByGovernorate: Record<string, number>;
  };
}

export const getOverviewParticipants = async () => {
  const { data } = await apiClient.get<OverviewParticipantsPayload>(
    "/gew-overview/participants",
  );
  return data;
};

export const getOverviewPartners = async () => {
  const { data } = await apiClient.get<OverviewPartnersPayload>(
    "/gew-overview/partners",
  );
  return data;
};

export interface AttendeeGenderSplit {
  female: number;
  male: number;
  unspecified: number;
  total: number;
}
